/**
 * ChangePasswordPage — authenticated admin route.
 * Submits to /api/auth/change-password.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, Check, AlertTriangle, ShieldCheck, ArrowLeft } from 'lucide-react';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (!detail) return 'कुछ गलत हो गया, कृपया पुनः प्रयास करें।';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e?.msg || JSON.stringify(e))).join(' ');
  return String(detail);
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ c: false, n: false, k: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false);

    if (next !== confirm) { setError('नया पासवर्ड और पुष्टि पासवर्ड समान नहीं हैं।'); return; }
    if (next === current) { setError('नया पासवर्ड पुराने से अलग होना चाहिए।'); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: current, new_password: next, confirm_password: confirm },
      );
      // 🔒 Backend already rotated the httpOnly cookie in the same response.
      // We deliberately DO NOT persist `data.token` to localStorage anymore —
      // relying on the cookie alone closes the XSS-exfil vector (SEC audit
      // Feb 07, 2026). The `data.token` field will be removed in a future
      // backend cleanup; ignoring it here is safe today.
      void data;
      setSuccess(true);
      setTimeout(() => navigate('/admin/dashboard'), 2500);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary cosmic-bg-alt text-text-main flex flex-col">
      <SEO title="पासवर्ड बदलें · ViratAstro Admin" noIndex />

      <div className="container-custom py-8 flex-1">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-6 text-sm" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Admin Dashboard
        </Link>

        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-heading text-2xl text-text-main">पासवर्ड बदलें</h1>
            <p className="text-text-muted text-sm mt-1">अपने Admin account का पासवर्ड सुरक्षित रूप से update करें</p>
          </div>

          {success ? (
            <div className="glass-card p-6 text-center" data-testid="success-card">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-emerald-300" />
              </div>
              <h2 className="font-heading text-lg text-emerald-300">पासवर्ड सफलतापूर्वक बदल गया!</h2>
              <p className="text-text-muted text-sm mt-2">अन्य सभी सक्रिय sessions logout कर दिए गए हैं। आप 2-3 सेकंड में dashboard पर redirect हो जाएँगे...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="glass-card p-6 space-y-4" data-testid="change-password-form">
              {[
                { key: 'c', val: current, set: setCurrent, label: 'वर्तमान पासवर्ड', testid: 'current-password-input', auto: 'current-password' },
                { key: 'n', val: next,    set: setNext,    label: 'नया पासवर्ड',     testid: 'new-password-input',     auto: 'new-password' },
                { key: 'k', val: confirm, set: setConfirm, label: 'पुष्टि पासवर्ड',   testid: 'confirm-password-input', auto: 'new-password' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-wider text-gold/80 font-semibold">{f.label}</label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted/60" />
                    <input
                      type={show[f.key] ? 'text' : 'password'}
                      value={f.val}
                      onChange={(e) => f.set(e.target.value)}
                      autoComplete={f.auto}
                      data-testid={f.testid}
                      required
                      className="w-full bg-black/30 border border-white/15 rounded-lg pl-10 pr-10 py-2.5 text-text-main focus:outline-none focus:border-gold/50"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(s => ({ ...s, [f.key]: !s[f.key] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted/60 hover:text-gold"
                      aria-label="Toggle password visibility"
                    >
                      {show[f.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}

              <PasswordStrengthMeter password={next} />

              {error && (
                <div className="bg-rose-500/15 border border-rose-400/40 rounded-lg p-3 text-rose-300 text-sm flex gap-2" data-testid="error-msg">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !current || !next || !confirm}
                data-testid="submit-change-password"
                className="w-full btn-gold py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'बदला जा रहा है...' : 'पासवर्ड बदलें'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
